import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { loginAdmin, fetchMe, logoutAdmin, registerUser, googleSession, markOnboarded, verifyEmail } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — let AuthCallback (/auth/google) exchange the code first.
    if (window.location.pathname === "/auth/google") {
      setReady(true);
      return;
    }
    const token = localStorage.getItem("dharmartha_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    fetchMe()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem("dharmartha_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await loginAdmin(email, password);
    localStorage.setItem("dharmartha_token", data.token);
    setUser(data.user);
    // Refresh with full profile incl. effective permissions (login response omits them).
    try { const full = await fetchMe(); setUser(full); return full; } catch { return data.user; }
  }, []);

  const register = useCallback(async (name, email, password, referralCode = "") => {
    // Signup now requires email verification — no token is returned; the user must
    // confirm via the emailed link before they can sign in.
    const data = await registerUser(name, email, password, referralCode);
    if (data.token) {
      localStorage.setItem("dharmartha_token", data.token);
      setUser(data.user);
    }
    return data;
  }, []);

  const verifyEmailToken = useCallback(async (token) => {
    const data = await verifyEmail(token);
    localStorage.setItem("dharmartha_token", data.token);
    setUser(data.user);
    try { const full = await fetchMe(); setUser(full); return full; } catch { return data.user; }
  }, []);

  const completeGoogle = useCallback(async (code, redirectUri) => {
    const data = await googleSession(code, redirectUri);
    localStorage.setItem("dharmartha_token", data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await logoutAdmin(); } catch { /* ignore */ }
    localStorage.removeItem("dharmartha_token");
    setUser(false);
  }, []);

  // Mark the welcome tour as complete (per-account, persists server-side).
  const completeOnboarding = useCallback(async () => {
    setUser((u) => (u ? { ...u, onboarded: true } : u));
    try { await markOnboarded(); } catch { /* best effort */ }
  }, []);

  return (
    <AuthContext.Provider value={{ user, ready, login, register, verifyEmailToken, completeGoogle, logout, completeOnboarding }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
